import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  Alert,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Autocomplete from '@mui/material/Autocomplete';

const specialistOptions = [
  { label: 'Psychiatrist', value: 'Psychiatrist' },
  { label: 'Psychologist', value: 'Psychologist' },
  { label: 'Neurologist', value: 'Neurologist' },
  { label: 'Cardiologist', value: 'Cardiologist' },
  { label: 'Dermatologist', value: 'Dermatologist' },
  { label: 'Orthopedist', value: 'Orthopedist' },
  { label: 'Ophthalmologist', value: 'Ophthalmologist' },
  { label: 'ENT Specialist', value: 'ENT Specialist' },
  { label: 'Gynecologist', value: 'Gynecologist' },
  { label: 'Pediatrician', value: 'Pediatrician' },
  { label: 'General Surgeon', value: 'General Surgeon' },
  { label: 'Urologist', value: 'Urologist' },
  { label: 'Endocrinologist', value: 'Endocrinologist' },
  { label: 'Oncologist', value: 'Oncologist' },
  { label: 'Pulmonologist', value: 'Pulmonologist' },
  { label: 'Rheumatologist', value: 'Rheumatologist' },
  { label: 'Other', value: 'Other' },
];

const ReferralsForm = ({ referrals = [], setReferrals }) => {
  const [newReferral, setNewReferral] = useState({
    referred_to: '',
    specialist: '', // For backward compatibility
    reason: '',
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [showError, setShowError] = useState(false);

  // Normalize referrals prop to handle different formats
  const normalizedReferrals = useMemo(() => {
    if (!referrals || referrals.length === 0) return [];
    return referrals.map(r => ({
      referred_to: r.referred_to || r.specialist || '',
      specialist: r.specialist || r.referred_to || '', // Keep both for compatibility
      reason: r.reason || '',
    }));
  }, [referrals]);

  // Find the option that matches the current referral
  const getCurrentOption = useCallback(() => {
    const value = newReferral.referred_to || newReferral.specialist;
    if (!value) return null;
    return specialistOptions.find(opt => opt.value === value || opt.label === value) || null;
  }, [newReferral.referred_to, newReferral.specialist]);

  const validateReferral = useCallback((referral) => {
    const newErrors = {};
    
    if (!referral.referred_to || referral.referred_to.trim() === '') {
      newErrors.referred_to = 'Specialist type is required';
    }
    
    if (!referral.reason || referral.reason.trim() === '') {
      newErrors.reason = 'Reason for referral is required';
    }
    
    return newErrors;
  }, []);

  const handleSpecialistChange = useCallback((event, newValue) => {
    const specialist = newValue 
      ? (typeof newValue === 'string' ? newValue : (newValue.value || newValue.label || ''))
      : '';
    setNewReferral(prev => ({
      ...prev,
      referred_to: specialist,
      specialist: specialist, // Sync both fields
    }));
    // Clear error when user starts typing
    if (errors.referred_to) {
      setErrors(prev => ({ ...prev, referred_to: null }));
    }
  }, [errors.referred_to]);

  const handleReasonChange = useCallback((e) => {
    const value = e.target.value;
    setNewReferral(prev => ({ ...prev, reason: value }));
    // Clear error when user starts typing
    if (errors.reason) {
      setErrors(prev => ({ ...prev, reason: null }));
    }
  }, [errors.reason]);

  const addOrUpdateReferral = useCallback(() => {
    const validationErrors = validateReferral(newReferral);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setShowError(true);
      return;
    }

    const referralToAdd = {
      referred_to: newReferral.referred_to.trim() || newReferral.specialist.trim(),
      specialist: newReferral.specialist.trim() || newReferral.referred_to.trim(), // Keep both
      reason: newReferral.reason.trim(),
    };

    if (editingIndex !== null) {
      const updatedReferrals = [...normalizedReferrals];
      updatedReferrals[editingIndex] = referralToAdd;
      setReferrals(updatedReferrals);
      setEditingIndex(null);
    } else {
      setReferrals([...normalizedReferrals, referralToAdd]);
    }

    // Reset form
    setNewReferral({
      referred_to: '',
      specialist: '',
      reason: '',
    });
    setErrors({});
    setShowError(false);
  }, [newReferral, editingIndex, normalizedReferrals, setReferrals, validateReferral]);

  const editReferral = useCallback((index) => {
    const referral = normalizedReferrals[index];
    setNewReferral({
      referred_to: referral.referred_to || referral.specialist || '',
      specialist: referral.specialist || referral.referred_to || '',
      reason: referral.reason || '',
    });
    setEditingIndex(index);
    setErrors({});
    setShowError(false);
  }, [normalizedReferrals]);

  const deleteReferral = useCallback((index) => {
    const updatedReferrals = normalizedReferrals.filter((_, i) => i !== index);
    setReferrals(updatedReferrals);
    
    // Reset editing if deleting the item being edited
    if (editingIndex === index) {
      setEditingIndex(null);
      setNewReferral({
        referred_to: '',
        specialist: '',
        reason: '',
      });
      setErrors({});
      setShowError(false);
    }
  }, [normalizedReferrals, setReferrals, editingIndex]);

  const handleCancel = useCallback(() => {
    setEditingIndex(null);
    setNewReferral({
      referred_to: '',
      specialist: '',
      reason: '',
    });
    setErrors({});
    setShowError(false);
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant='h6'>Referrals</Typography>
        {normalizedReferrals.length > 0 && (
          <Chip 
            label={`${normalizedReferrals.length} referral${normalizedReferrals.length !== 1 ? 's' : ''}`} 
            color="primary" 
            size="small" 
          />
        )}
      </Box>

      {showError && Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setShowError(false)}>
          Please fix the following errors:
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            {Object.values(errors).filter(Boolean).map((error, idx) => (
              <li key={idx}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Autocomplete
        freeSolo
        options={specialistOptions}
        getOptionLabel={(option) => typeof option === 'string' ? option : (option.label || option.value || '')}
        value={getCurrentOption() || newReferral.referred_to || newReferral.specialist || null}
        onChange={handleSpecialistChange}
        onInputChange={(event, newInputValue, reason) => {
          // Handle freeSolo text input
          if (reason === 'input' && event && event.type === 'change') {
            handleSpecialistChange(event, newInputValue);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Referred To (Specialist)'
            placeholder='Select or enter specialist type'
            required
            error={!!errors.referred_to}
            helperText={errors.referred_to || 'Select a specialist type or enter a custom one'}
            sx={{ marginBottom: 2 }}
            fullWidth
          />
        )}
      />
      
      <TextField
        fullWidth
        label='Reason for Referral'
        placeholder='Enter the reason for referring to this specialist'
        required
        multiline
        rows={2}
        value={newReferral.reason}
        onChange={handleReasonChange}
        error={!!errors.reason}
        helperText={errors.reason || 'Explain why the patient is being referred'}
        sx={{ marginBottom: 2 }}
      />
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant='contained'
          color='primary'
          startIcon={editingIndex !== null ? <EditIcon /> : <AddIcon />}
          onClick={addOrUpdateReferral}
        >
          {editingIndex !== null ? 'Update Referral' : 'Add Referral'}
        </Button>
        {editingIndex !== null && (
          <Button
            variant='outlined'
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
      </Box>

      {normalizedReferrals.length > 0 ? (
        <Box sx={{ marginTop: 3 }}>
          {normalizedReferrals.map((referral, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                padding: 2,
                marginBottom: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                border: editingIndex === index ? '2px solid' : '1px solid',
                borderColor: editingIndex === index ? 'primary.main' : 'divider',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 4,
                },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 0.5 }}>
                  {referral.referred_to || referral.specialist || 'Unnamed Specialist'}
                </Typography>
                <Typography variant='body2' color="text.secondary">
                  <strong>Reason:</strong> {referral.reason || 'Not specified'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  color='primary'
                  size="small"
                  onClick={() => editReferral(index)}
                  disabled={editingIndex !== null && editingIndex !== index}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color='error'
                  size="small"
                  onClick={() => deleteReferral(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <Typography variant='body2' color="text.secondary">
            No referrals added yet. Add a referral above.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ReferralsForm;
